import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './AddCarForm.css';

const AddCarForm = ({ onSubmit, initialData = {}, onCancel }) => {
    /**
     * Process initial data dates - separate date and time components
     */
    const processInitialDateTime = (dateTimeString) => {
        if (!dateTimeString) return { date: null, time: null };

        const dateTime = new Date(dateTimeString);
        if (isNaN(dateTime.getTime())) return { date: null, time: null };

        // Create date-only object (midnight local time)
        const dateOnly = new Date(dateTime.getFullYear(), dateTime.getMonth(), dateTime.getDate());

        // Check if original has time component (not midnight)
        const hasTime = dateTime.getHours() !== 0 || dateTime.getMinutes() !== 0 || dateTime.getSeconds() !== 0;
        const timeOnly = hasTime ? dateTime : null;

        return { date: dateOnly, time: timeOnly };
    };

    const pickupProcessed = processInitialDateTime(initialData.pickupTime);
    const returnProcessed = processInitialDateTime(initialData.returnTime);

    const [formData, setFormData] = useState({
        rentalCompany: initialData.rentalCompany || '',
        bookingReference: initialData.bookingReference || '',
        pickupLocation: initialData.pickupLocation || '',
        returnLocation: initialData.returnLocation || '',
        pickupDate: pickupProcessed.date,
        pickupTime: pickupProcessed.time,
        returnDate: returnProcessed.date,
        returnTime: returnProcessed.time,
        vehicleClass: initialData.vehicleClass || '',
        notes: initialData.notes || ''
    });

    const [errors, setErrors] = useState({});
    const [carTitle, setCarTitle] = useState('');

    // Update title when relevant fields change
    useEffect(() => {
        let title = '';

        if (formData.rentalCompany) {
            title = formData.rentalCompany;
        }

        if (formData.pickupLocation || formData.returnLocation) {
            if (title) title += ': ';
            const pickup = formData.pickupLocation || 'Pickup';
            const returnLoc = formData.returnLocation || 'Return';
            title += `${pickup} to ${returnLoc}`;
        }

        setCarTitle(title || 'Car Rental');
    }, [formData.rentalCompany, formData.pickupLocation, formData.returnLocation]);

    // Preset return date/time when pickup is selected
    useEffect(() => {
        if (formData.pickupDate && !formData.returnDate) {
            setFormData(prev => ({
                ...prev,
                returnDate: formData.pickupDate
            }));
        }
        if (formData.pickupTime && !formData.returnTime) {
            setFormData(prev => ({
                ...prev,
                returnTime: formData.pickupTime
            }));
        }
    }, [formData.pickupDate, formData.pickupTime]);

    const handleInputChange = (fieldName, value) => {
        setFormData(prev => ({
            ...prev,
            [fieldName]: value
        }));

        // Clear error for this field when it's changed
        if (errors[fieldName]) {
            setErrors(prev => ({
                ...prev,
                [fieldName]: null
            }));
        }
    };

    /**
     * Date Processing Helper
     * Combines separate date and time selections into appropriate format
     */
    const formatDateForSubmission = (date, time) => {
        if (!date) return null;

        if (!time) {
            // Date only - store as YYYY-MM-DD to avoid timezone issues
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        } else {
            // Date + Time - combine them and store as ISO string
            const combinedDateTime = new Date(
                date.getFullYear(),
                date.getMonth(),
                date.getDate(),
                time.getHours(),
                time.getMinutes(),
                time.getSeconds()
            );
            return combinedDateTime.toISOString();
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // At least rental company or pickup location is required
        if (!formData.rentalCompany && !formData.pickupLocation) {
            newErrors.form = 'At least Rental Company or Pickup Location is required.';
        }

        // Validate return date is after pickup date if both are present
        if (formData.pickupDate && formData.returnDate && formData.pickupDate > formData.returnDate) {
            newErrors.returnDate = 'Return date must be after pickup date.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        const dataToSubmit = {
            details: {
                rentalCompany: formData.rentalCompany || '',
                bookingReference: formData.bookingReference || '',
                pickupLocation: formData.pickupLocation || '',
                returnLocation: formData.returnLocation || '',
                pickupTime: formatDateForSubmission(formData.pickupDate, formData.pickupTime),
                returnTime: formatDateForSubmission(formData.returnDate, formData.returnTime),
                vehicleClass: formData.vehicleClass || '',
                notes: formData.notes || ''
            },
            summary: carTitle,
            status: 'Planned'
        };

        console.log('Submitting car rental data:', dataToSubmit);
        onSubmit(dataToSubmit);
    };

    const getFieldError = (fieldName) => {
        return errors[fieldName] || null;
    };

    const renderFieldError = (fieldName) => {
        const error = getFieldError(fieldName);
        return error ? <div className="error-message">{error}</div> : null;
    };

    return (
        <form onSubmit={handleSubmit} className="car-form">
            {/* Title Section */}
            <div className="car-title">
                <h3>Car Rental Details</h3>
                <div className={`car-title-display ${!carTitle ? 'car-title-placeholder' : ''}`}>
                    {carTitle || 'Enter car rental details to generate title'}
                </div>
            </div>

            {/* Form-level error */}
            {errors.form && <div className="error-message form-error">{errors.form}</div>}

            {/* Row 1: Rental Company + Booking Reference */}
            <div className="form-section">
                <div className="form-row">
                    <div className="form-group">
                        <label>Rental Company</label>
                        <input
                            type="text"
                            value={formData.rentalCompany}
                            onChange={(e) => handleInputChange('rentalCompany', e.target.value)}
                            placeholder="e.g. Hertz, Enterprise, Avis"
                        />
                    </div>

                    <div className="form-group">
                        <label>Booking Reference</label>
                        <input
                            type="text"
                            value={formData.bookingReference}
                            onChange={(e) => handleInputChange('bookingReference', e.target.value)}
                            placeholder="Confirmation number"
                        />
                    </div>
                </div>

                {/* Row 2: Pickup Location + Pickup Date/Time */}
                <div className="form-row">
                    <div className="form-group">
                        <label>Pickup Location</label>
                        <input
                            type="text"
                            value={formData.pickupLocation}
                            onChange={(e) => handleInputChange('pickupLocation', e.target.value)}
                            placeholder="e.g. Los Angeles Airport"
                        />
                    </div>

                    <div className="form-group date-time-group">
                        <label>Pickup Date & Time</label>
                        <div className="date-time-row">
                            <DatePicker
                                selected={formData.pickupDate}
                                onChange={(date) => handleInputChange('pickupDate', date)}
                                dateFormat="d MMMM yyyy"
                                placeholderText="Select pickup date"
                                isClearable
                                className="date-picker-input"
                            />
                            <DatePicker
                                selected={formData.pickupTime}
                                onChange={(time) => handleInputChange('pickupTime', time)}
                                showTimeSelect
                                showTimeSelectOnly
                                timeIntervals={30}
                                timeCaption="Time"
                                dateFormat="h:mm aa"
                                placeholderText="Time"
                                isClearable
                                className="time-picker-input"
                            />
                        </div>
                    </div>
                </div>

                {/* Row 3: Return Location + Return Date/Time */}
                <div className="form-row">
                    <div className="form-group">
                        <label>Return Location</label>
                        <input
                            type="text"
                            value={formData.returnLocation}
                            onChange={(e) => handleInputChange('returnLocation', e.target.value)}
                            placeholder="e.g. San Francisco Airport"
                        />
                    </div>

                    <div className="form-group date-time-group">
                        <label>Return Date & Time</label>
                        <div className="date-time-row">
                            <DatePicker
                                selected={formData.returnDate}
                                onChange={(date) => handleInputChange('returnDate', date)}
                                dateFormat="d MMMM yyyy"
                                placeholderText="Select return date"
                                isClearable
                                className={`date-picker-input ${getFieldError('returnDate') ? 'error' : ''}`}
                            />
                            <DatePicker
                                selected={formData.returnTime}
                                onChange={(time) => handleInputChange('returnTime', time)}
                                showTimeSelect
                                showTimeSelectOnly
                                timeIntervals={30}
                                timeCaption="Time"
                                dateFormat="h:mm aa"
                                placeholderText="Time"
                                isClearable
                                className="time-picker-input"
                            />
                        </div>
                        {renderFieldError('returnDate')}
                    </div>
                </div>

                {/* Row 4: Vehicle Class (full width) */}
                <div className="form-group full-width">
                    <label>Vehicle Class</label>
                    <input
                        type="text"
                        value={formData.vehicleClass}
                        onChange={(e) => handleInputChange('vehicleClass', e.target.value)}
                        placeholder="e.g. Economy, Compact, SUV, Luxury"
                    />
                </div>

                {/* Notes */}
                <div className="form-group full-width">
                    <label>Notes</label>
                    <textarea
                        value={formData.notes}
                        onChange={(e) => handleInputChange('notes', e.target.value)}
                        placeholder="Additional notes about the car rental"
                        rows={3}
                    />
                </div>
            </div>

            {/* Form Actions */}
            <div className="form-actions">
                <button type="button" className="cancel-button" onClick={onCancel}>Cancel</button>
                <button type="submit" className="save-button">Save</button>
            </div>
        </form>
    );
};

export default AddCarForm;
